import { useAvailableWorkspaces, useCurrentOrganization } from "@ice/react";

type AvailableWorkspaces = ReturnType<typeof useAvailableWorkspaces>;

export type TenantWorkspace = AvailableWorkspaces["workspaces"][number];

interface UseTenantWorkspaceResult {
    workspace: TenantWorkspace | undefined;
    isLoading: boolean;
    isError: boolean;
    error: string | null;
}

/**
 * The app's "tenant" workspace: the one belonging to the current organization.
 *
 * `useCurrentWorkspace` returns the workspace selected in the shell, which may
 * be a client's: that is not the tenant the app must operate on. The SDK fetches
 * the available workspaces with `ownerId = organizationId`, so the
 * organization's own workspace is the one with `itemId === organization.id`.
 *
 * `isLoading` covers both the list being fetched and the organization not being
 * resolved yet: both SDK hooks start empty on every mount and fill in from an
 * effect (see useWorkspaceId), so the first render is always loading. Consumers
 * must not read anything into `workspace` being undefined while `isLoading` is true.
 */
export const useTenantWorkspace = (): UseTenantWorkspaceResult => {
    const [organization] = useCurrentOrganization();
    const { workspaces, isLoading, isError, error } = useAvailableWorkspaces();

    const workspace = organization
        ? workspaces.find(({ itemId }) => itemId === organization.id)
        : undefined;

    return {
        workspace,
        isLoading: isLoading || organization === null,
        isError,
        error,
    };
};
